// pages/DataManagementPage.js - 数据管理
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Table, DatePicker, Button, Tag, Row, Col, message } from 'antd';
import { DatabaseOutlined, LineChartOutlined, ReloadOutlined, DownloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import { api } from '../services/api';

const { RangePicker } = DatePicker;

const cardStyle = {
  background: 'linear-gradient(135deg, #0d1f44 0%, #132c5e 100%)',
  border: '1px solid rgba(0, 180, 216, 0.3)',
  borderRadius: 8,
  boxShadow: '0 0 20px rgba(0, 180, 216, 0.1)',
};
const COLORS = { primary: '#00d4ff', solar: '#f4c542', storage: '#52e8b0', text: '#fff', muted: 'rgba(255,255,255,0.6)' };

const DataManagementPage = () => {
  const { t } = useTranslation();
  const [dailyData, setDailyData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState({
    device_id: '', start_date: moment().subtract(7, 'days').format('YYYY-MM-DD'),
    end_date: moment().format('YYYY-MM-DD')
  });

  const fetchDaily = useCallback(async () => {
    setLoading(true);
    try {
      const json = await api.getPowerDaily({
        device_id: query.device_id || undefined,
        start_date: query.start_date,
        end_date: query.end_date,
      });
      if (json.success) setDailyData(json.data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [query.device_id, query.start_date, query.end_date]);

  useEffect(() => { fetchDaily(); }, [fetchDaily]);

  const columns = [
    { title: t('data.date'), dataIndex: 'date', key: 'date', render: val => <span style={{ color: COLORS.primary }}>{moment(val).format('YYYY-MM-DD')}</span> },
    { title: t('data.device'), dataIndex: 'device_name', key: 'device_name', render: val => <span style={{ color: COLORS.text }}>{val}</span> },
    { title: t('data.dailyPowerKwh'), dataIndex: 'power', key: 'power', render: v => <Tag color="blue">{v} kWh</Tag> },
    { title: t('data.maxPowerKw'), dataIndex: 'max_power', key: 'max_power', render: v => <span style={{ color: COLORS.solar }}>{v}</span> },
    { title: t('data.avgPowerKw'), dataIndex: 'avg_power', key: 'avg_power', render: v => <span style={{ color: COLORS.muted }}>{v}</span> },
    { title: t('data.runtimeMin'), dataIndex: 'runtime', key: 'runtime', render: v => <span style={{ color: COLORS.muted }}>{v}</span> },
  ];

  const totalPower = dailyData.reduce((sum, d) => sum + parseFloat(d.power || 0), 0);

  // CSV导出
  const handleExport = () => {
    if (dailyData.length === 0) { message.warning(t('data.noDataToExport')); return; }
    const headers = [t('data.date'), t('data.device'), t('data.dailyPowerKwh'), t('data.maxPowerKw'), t('data.avgPowerKw'), t('data.runtimeMin')];
    const rows = dailyData.map(d => [
      d.date ? moment(d.date).format('YYYY-MM-DD') : '',
      d.device_name || '',
      d.power || 0,
      d.max_power || 0,
      d.avg_power || 0,
      d.runtime || 0,
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const BOM = '﻿';
    const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${t('data.exportFileName')}_${query.start_date}_${query.end_date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    message.success(t('data.exportSuccess'));
  };

  return (
    <div style={{ minHeight: '100%' }}>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Card variant="borderless" style={cardStyle} styles={{ body: { padding: '20px' } }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ width: 48, height: 48, borderRadius: 10, background: 'rgba(0,180,216,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(0,180,216,0.3)' }}>
                <LineChartOutlined style={{ fontSize: 22, color: COLORS.primary }} />
              </div>
              <div>
                <div style={{ fontSize: 12, color: COLORS.muted }}>{t('data.intervalTotalGeneration')}</div>
                <div style={{ fontSize: 24, fontWeight: 700, color: COLORS.primary }}>{totalPower.toFixed(2)} <span style={{ fontSize: 12, color: COLORS.muted }}>kWh</span></div>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card variant="borderless" style={cardStyle} styles={{ body: { padding: '20px' } }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ width: 48, height: 48, borderRadius: 10, background: 'rgba(244,197,66,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(244,197,66,0.3)' }}>
                <DatabaseOutlined style={{ fontSize: 22, color: COLORS.solar }} />
              </div>
              <div>
                <div style={{ fontSize: 12, color: COLORS.muted }}>{t('data.recordCount')}</div>
                <div style={{ fontSize: 24, fontWeight: 700, color: COLORS.solar }}>{dailyData.length} <span style={{ fontSize: 12, color: COLORS.muted }}>{t('data.records')}</span></div>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card variant="borderless" style={cardStyle} styles={{ body: { padding: '20px' } }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: COLORS.muted, fontSize: 12, whiteSpace: 'nowrap' }}>{t('data.dateRange')}:</span>
              <RangePicker
                value={[moment(query.start_date), moment(query.end_date)]}
                onChange={(dates) => {
                  if (dates) setQuery(q => ({ ...q, start_date: dates[0].format('YYYY-MM-DD'), end_date: dates[1].format('YYYY-MM-DD') }));
                }}
                style={{ background: 'rgba(0,0,0,0.2)', color: '#fff' }}
              />
              <Button icon={<ReloadOutlined />} onClick={fetchDaily}
                style={{ background: 'rgba(0,180,216,0.15)', color: COLORS.primary, border: '1px solid rgba(0,180,216,0.3)' }}>{t('data.refresh')}</Button>
              <Button icon={<DownloadOutlined />} onClick={handleExport}
                style={{ background: 'rgba(0,180,216,0.15)', color: COLORS.primary, border: '1px solid rgba(0,180,216,0.3)' }}>{t('data.exportCsv')}</Button>
            </div>
          </Card>
        </Col>
      </Row>

      <Card variant="borderless" style={cardStyle} styles={{ body: { padding: 20 } }}>
        <Table columns={columns} dataSource={dailyData} loading={loading} rowKey="id" pagination={{ pageSize: 10 }}
          style={{ background: 'transparent' }}
        />
      </Card>
    </div>
  );
};

export default DataManagementPage;
